import { useEffect, useState, useRef, useLayoutEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { SaudiRiyal, ArrowRight, ShoppingBag, Check, Info, Trash2, AlertTriangle, Plus, Minus, X, Clock } from 'lucide-react'
import toast from 'react-hot-toast'
import { TOAST_STYLE, TOAST_ERROR_ICON, TOAST_SUCCESS_ICON } from '../components/toast/AppToaster'
import { TapCardV2 } from '../components/payments/TapCardV2'
import { TapApplePay } from '../components/payments/TapApplePay'
import {
  usePaymentStore,
  PAYMENT_STEPS,
  PAYMENT_METHODS,
  CHECKOUT_STAGE_QUERY_KEY,
  CHECKOUT_URL_STAGES,
  paymentStepToUrlStage,
  urlStageToPaymentStep,
} from '../store/paymentStore'
import { useCartStore } from '../store/useCartStore'
import { useStoreInfo } from '../store/useStoreInfo'
import visaMasterImg from '../assets/payments/visa-master-png.png'
import applePayLogo from '../assets/payments/apple-pay.svg'
import * as S from './CheckoutStyles'
import { eventHub } from '../lib/tracking/event-hub'
import { persistPurchaseDeviceContext } from '../lib/tracking/deviceContext'
import { normalizeMediaUrl } from '../lib/mediaUrl'
import { resolveFreeShipping } from '../lib/offers'
import confetti from 'canvas-confetti'

export default function CheckoutPage() {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const { items: cartItems, total: cartTotal, removeItem, count: cartCount, syncPrices, updateQuantity } = useCartStore()
  const { data: storeData, fetchStoreData } = useStoreInfo()
  const {
    step,
    paymentMethod,
    paymentResult,
    isProcessing,
    setPaymentMethod,
    setStep,
    goBack,
    fetchMerchantInfo,
    processTapPayment,
    setPaymentResult,
    checkPaymentStatus,
    resetPayment,
    customerDetails,
    setCustomerDetails,
    submitCustomerDetails,
  } = usePaymentStore()

  // ... existing hooks ...

  const [itemToDelete, setItemToDelete] = useState<string | null>(null)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [errors, setErrors] = useState<Record<string, string | null>>({})
  const [isSafari, setIsSafari] = useState(false)
  const [editingItemId, setEditingItemId] = useState<string | null>(null)
  const [couponCode, setCouponCode] = useState<string>('')
  const [discount, setDiscount] = useState<number>(0)
  const [appliedCoupon, setAppliedCoupon] = useState<string | null>(null)
  const [freeShipping, setFreeShipping] = useState<boolean>(false)
  const [isValidatingCoupon, setIsValidatingCoupon] = useState<boolean>(false)
  const [isSubmittingCustomer, setIsSubmittingCustomer] = useState(false)
  const [busyModalDismissed, setBusyModalDismissed] = useState(false)
  const lastTrackedPaymentMethod = useRef<string | null>(null) // ✅ لتجنب تكرار ADD_BILLING
  const hasTrackedCheckout = useRef(false) // ✅ لتجنب تكرار IDENTIFY و START_CHECKOUT
  const prevCartTotalRef = useRef<number>(0) // لتشغيل الكونفيتي عند الوصول لشحن مجاني

  useEffect(() => {
    // Check if browser is Safari
    const isSafariCheck = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
    setIsSafari(isSafariCheck);
  }, []);

  useEffect(() => {
    // عنوان الصفحة يُحدّث ديناميكياً من App.tsx حسب اسم المتجر
    // ✅ إعادة تعيين تتبع الأحداث عند تحميل الصفحة
    lastTrackedPaymentMethod.current = null;
    hasTrackedCheckout.current = false;
    
    // ✅ إلغاء تحديد طريقة الدفع عند تحديث الصفحة
    if (paymentMethod) {
      setPaymentMethod(null);
      // إذا كنا في خطوة CARD_FORM، نرجع إلى خطوة PAYMENT_METHOD
      if (step === PAYMENT_STEPS.CARD_FORM) {
        setStep(PAYMENT_STEPS.PAYMENT_METHOD);
      }
    }
    
    fetchMerchantInfo()
    fetchStoreData()

    // Sync prices and remove sold-out items; notify if any were removed
    ;(async () => {
      const removed = await syncPrices()
      if (removed.length > 0) {
        const names = removed.map(r => r.title).join('، ')
        toast.error(`تمت إزالة من السلة (نفذت): ${names}`, {
          id: 'sync-removed',
          duration: 6000,
          style: TOAST_STYLE,
          iconTheme: TOAST_ERROR_ICON,
        })
      }
    })()

    // تعيين القيمة الافتراضية للمدينة بعد تحميل بيانات المتجر

    const params = new URLSearchParams(window.location.search)
    const tapId = params.get('tap_id')
    if (tapId) {
      params.delete('tap_id')
      const q = params.toString()
      window.history.replaceState(
        {},
        document.title,
        q ? `${window.location.pathname}?${q}` : window.location.pathname
      )
      checkPaymentStatus(tapId)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // ✅ عند الدخول لخطوة الدفع: مزامنة السلة وإزالة المنتجات النفذة فوراً
  useEffect(() => {
    if (step !== PAYMENT_STEPS.PAYMENT_METHOD && step !== PAYMENT_STEPS.CARD_FORM) return
    ;(async () => {
      const removed = await syncPrices()
      if (removed.length > 0) {
        const names = removed.map(r => r.title).join('، ')
        toast.error(`تمت إزالة من السلة (نفذت): ${names}`, {
          id: 'sync-removed',
          duration: 6000,
          style: TOAST_STYLE,
          iconTheme: TOAST_ERROR_ICON,
        })
      }
    })()
  }, [step])

  // ✅ استخدام useRef لتجنب التنقل المتكرر (حل مشكلة React error #300)
  const hasNavigatedRef = useRef(false)
  
  // ✅ إعادة تعيين hasNavigatedRef عندما يتم مسح paymentResult (بدء عملية دفع جديدة)
  useEffect(() => {
    if (!paymentResult) {
      hasNavigatedRef.current = false
    }
  }, [paymentResult])
  
  // ✅ استخدام useRef لتخزين navigate function لتجنب React error #300
  const navigateRef = useRef(navigate)
  useEffect(() => {
    navigateRef.current = navigate
  }, [navigate])

  // ✅ المتجر مشغول (ضغط على الطلبات): يوقف الدفع ويظهر تنبيهاً للعميل
  const storeBusy = storeData?.busy === true
  // إعادة إظهار التنبيه إذا عاد المتجر للحالة مشغول بعد أن أُغلق التنبيه
  useEffect(() => {
    if (!storeBusy) setBusyModalDismissed(false)
  }, [storeBusy])

  // تعيين المدينة الافتراضية من الفرع الافتراضي بعد تحميل بيانات المتجر
  useEffect(() => {
    if (!storeData?.branches?.length) return
    if (customerDetails.address.city) return
    const availableBranches = storeData.branches.filter(b => !b.comingSoon)
    const defaultBranch = availableBranches.find(b => b.isDefault) || availableBranches[0]
    if (defaultBranch) {
      setCustomerDetails({ address: { ...customerDetails.address, city: defaultBranch.name } })
    }
  }, [storeData])

  const backToStore = () => {
    resetPayment()
    navigate('/', { replace: true })
  }

  const handleRemoveItem = (id: string) => {
    setItemToDelete(id)
    setShowDeleteConfirm(true)
  }

  const confirmDelete = () => {
    if (itemToDelete) {
      removeItem(itemToDelete)
      setItemToDelete(null)
    }
    setShowDeleteConfirm(false)
  }

  const handleEditQuantity = (itemId: string) => {
    setEditingItemId(itemId)
  }

  const handleUpdateQuantity = (itemId: string, newQuantity: number) => {
    if (newQuantity <= 0) {
      handleRemoveItem(itemId)
      setEditingItemId(null)
    } else {
      updateQuantity(itemId, newQuantity)
    }
  }

  const handleCancelEdit = () => {
    setEditingItemId(null)
  }

  const shipping = storeData?.shipping
  const defaultShippingCost = shipping ?? 0
  const safeCartTotal = Number(cartTotal) || 0
  // إعدادات الشحن المجاني من السيرفر فقط — لا نعرض شيئاً حتى نستلم الاستجابة
  const hasStoreData = storeData != null
  // منطق الشحن المجاني في مكان واحد (lib/offers) — تستخدمه البطاقة والملخص معاً
  const freeShippingInfo = resolveFreeShipping({
    storeReady: hasStoreData,
    shipping,
    freeShippingActive: storeData?.freeShippingActive,
    freeShippingThreshold: storeData?.freeShippingThreshold,
    cartTotal: safeCartTotal,
    couponFreeShipping: freeShipping,
  })
  const freeShippingActive = freeShippingInfo.isOfferActive
  const freeShippingThreshold = freeShippingInfo.threshold
  const effectiveFreeShipping = freeShippingInfo.isFreeByOffer
  const shippingCost = freeShippingInfo.effectiveShippingCost
  const amountLeftForFreeShipping = freeShippingInfo.remaining

  // كونفيتي عند الوصول للشحن المجاني — فقط عندما العرض مفعّل من السيرفر
  useEffect(() => {
    if (cartTotal <= 0 || !freeShippingActive || defaultShippingCost <= 0) return
    const wasBelow = prevCartTotalRef.current < freeShippingThreshold
    const nowReached = cartTotal >= freeShippingThreshold
    if (wasBelow && nowReached) {
      prevCartTotalRef.current = cartTotal
      // الـ emoji في toast مخفي من قارئات الشاشة عبر aria-hidden
      toast.success('مبروك! التوصيل صار مجاني', { id: 'free-shipping' })
      const opts = {
        particleCount: 120,
        spread: 80,
        origin: { y: 0.65 },
        colors: ['oklch(0.65 0.24 33.09)', 'oklch(69.59% 0.149 162.48)', 'oklch(0.85 0.12 95)', 'oklch(0.7686 0.1647 70.08)', 'oklch(1 0 0)'],
        zIndex: 9998, // أقل من toast (9999) حتى لا يخفيه
      }
      const t = setTimeout(() => {
        confetti(opts)
        confetti({ ...opts, origin: { x: 0.25, y: 0.7 }, particleCount: 50 })
        confetti({ ...opts, origin: { x: 0.75, y: 0.7 }, particleCount: 50 })
      }, 100)
      return () => clearTimeout(t)
    }
    prevCartTotalRef.current = cartTotal
  }, [cartTotal, defaultShippingCost, freeShippingThreshold, freeShippingActive])

  // حساب المبلغ الإجمالي الفعلي
  // ✅ التأكد من أن جميع القيم أرقام صحيحة لتجنب NaN
  const safeShippingCost = Number(shippingCost) || 0
  const safeDiscount = Number(discount) || 0
  const calculatedTotal = safeCartTotal + safeShippingCost - safeDiscount
  // المبلغ الفعلي (نستخدمه للعرض والإرسال للباك إند)
  // ✅ المبالغ الصغيرة (> 0 و < 2.00) مسموح بها وستُرسل إلى Tap
  const total = isNaN(calculatedTotal) || calculatedTotal <= 0 ? 0.00 : calculatedTotal

  // وظيفة للتحقق من صحة الكوبون
  const validateCoupon = async (code: string, amount: number): Promise<{ valid: boolean; discount: number; freeShipping: boolean; message: string }> => {
    try {
      const BACKEND_URL = import.meta.env.VITE_BACKEND_URL || 'https://app.teen-sa.com'
      const response = await fetch(`${BACKEND_URL}/api/coupons/validate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          code: code.trim(),
          amount: amount,
        }),
      })

      const data = await response.json()

      if (data.success && data.data.valid) {
        return {
          valid: true,
          discount: data.data.discount_amount || 0, // ✅ fallback لـ 0 إذا كان undefined
          freeShipping: data.data.free_shipping || false,
          message: data.data.message || 'تم التحقق من كود الخصم بنجاح'
        }
      } else {
        return {
          valid: false,
          discount: 0,
          freeShipping: false,
          message: data.message || 'كود الخصم المدخل غير صحيح أو غير موجود'
        }
      }
    } catch (error) {
      return {
        valid: false,
        discount: 0,
        freeShipping: false,
        message: 'كود الخصم غير صالح'
      }
    }
  }

  const handleApplyCoupon = async () => {
    if (!couponCode.trim()) {
      toast.error('يرجى إدخال كود الخصم', {
        id: 'coupon-empty',
        style: TOAST_STYLE,
        iconTheme: TOAST_ERROR_ICON,
      })
      return
    }

    setIsValidatingCoupon(true)
    const result = await validateCoupon(couponCode, cartTotal)
    setIsValidatingCoupon(false)

    if (result.valid) {
      setDiscount(result.discount || 0) // ✅ fallback لـ 0
      setFreeShipping(result.freeShipping)
      setAppliedCoupon(couponCode.trim().toUpperCase())
      if (result.freeShipping && result.discount === 0) {
        toast.success('تم تطبيق كود الشحن المجاني بنجاح', {
          id: 'coupon-applied',
          style: TOAST_STYLE,
          iconTheme: TOAST_SUCCESS_ICON,
        })
      } else if (result.freeShipping) {
        toast.success(`${result.message} - الشحن مجاني`, {
          id: 'coupon-applied',
          style: TOAST_STYLE,
          iconTheme: TOAST_SUCCESS_ICON,
        })
      } else {
        toast.success(result.message, {
          id: 'coupon-applied',
          style: TOAST_STYLE,
          iconTheme: TOAST_SUCCESS_ICON,
        })
      }
    } else {
      toast.error(result.message, {
        id: 'coupon-error',
        style: TOAST_STYLE,
        iconTheme: TOAST_ERROR_ICON,
      })
    }
  }

  const handleRemoveCoupon = () => {
    setDiscount(0)
    setFreeShipping(false)
    setAppliedCoupon(null)
    setCouponCode('')
    toast.success('تم إلغاء كوبون الخصم', {
      id: 'coupon-removed',
      style: TOAST_STYLE,
      iconTheme: TOAST_SUCCESS_ICON,
    })
  }

  // التحقق من صحة الكوبون عند تغيير قيمة الطلب أو قبل الدفع
  useEffect(() => {
    if (appliedCoupon && cartTotal > 0) {
      const checkCoupon = async () => {
        const result = await validateCoupon(appliedCoupon, cartTotal)
        if (!result.valid) {
          // الكوبون لم يعد صالحاً - إزالته تلقائياً
          setDiscount(0)
          setAppliedCoupon(null)
          setCouponCode('')
          toast.error(result.message || 'عذراً، هذا الكوبون لم يعد صالحاً للاستخدام وتم إزالته', {
            id: 'coupon-expired',
            duration: 7000,
            style: TOAST_STYLE,
            iconTheme: TOAST_ERROR_ICON,
          })
        } else if ((result.discount || 0) !== discount) {
          // تحديث قيمة الخصم إذا تغيرت
          setDiscount(result.discount || 0)
        }
      }
      
      // التحقق بعد تأخير بسيط لتجنب الطلبات المتكررة
      const timeoutId = setTimeout(checkCoupon, 500)
      return () => clearTimeout(timeoutId)
    }
  }, [cartTotal, appliedCoupon]) // التحقق عند تغيير قيمة الطلب

  // مزامنة ‎?stage= مع خطوة الدفع (رجوع المتصفح يغيّر الرابط أولاً؛ المتابعة في النموذج تغيّر الخطوة أولاً)
  const prevCheckoutSearchRef = useRef<string | null>(null)
  const prevCheckoutStepRef = useRef(step)
  useEffect(() => {
    if (cartCount === 0) return

    const paramsString = searchParams.toString()
    const raw = searchParams.get(CHECKOUT_STAGE_QUERY_KEY)
    const stepStage = paymentStepToUrlStage(step)

    const hadPrev = prevCheckoutSearchRef.current !== null
    const searchChanged = hadPrev && paramsString !== prevCheckoutSearchRef.current
    const stepChanged = hadPrev && step !== prevCheckoutStepRef.current

    prevCheckoutSearchRef.current = paramsString
    prevCheckoutStepRef.current = step

    const validTarget = raw ? urlStageToPaymentStep(raw) : null

    if (!raw || validTarget === null) {
      setSearchParams(
        (prev) => {
          const p = new URLSearchParams(prev)
          if (p.get(CHECKOUT_STAGE_QUERY_KEY) === stepStage) return prev
          p.set(CHECKOUT_STAGE_QUERY_KEY, stepStage)
          return p
        },
        { replace: true }
      )
      return
    }

    if (raw === stepStage) return

    if (!hadPrev) {
      // أول تشغيل: رابط ‎?stage=payment مع مخزن على بيانات العميل → نثق بالرابط (مشاركة/إشارة مرجعية)
      if (raw === CHECKOUT_URL_STAGES.PAYMENT && stepStage === CHECKOUT_URL_STAGES.CUSTOMER) {
        setStep(PAYMENT_STEPS.PAYMENT_METHOD)
        return
      }
      // مخزن متقدّم لكن الرابط يقول customer → نبقي التقدم ونصحح الرابط
      if (raw === CHECKOUT_URL_STAGES.CUSTOMER && stepStage === CHECKOUT_URL_STAGES.PAYMENT) {
        setSearchParams(
          (prev) => {
            const p = new URLSearchParams(prev)
            p.set(CHECKOUT_STAGE_QUERY_KEY, stepStage)
            return p
          },
          { replace: true }
        )
      }
      return
    }

    if (searchChanged && !stepChanged) {
      if (raw === CHECKOUT_URL_STAGES.CUSTOMER) setStep(PAYMENT_STEPS.CUSTOMER_DETAILS)
      else if (raw === CHECKOUT_URL_STAGES.PAYMENT) setStep(PAYMENT_STEPS.PAYMENT_METHOD)
      return
    }

    if (stepChanged && !searchChanged) {
      setSearchParams(
        (prev) => {
          const p = new URLSearchParams(prev)
          if (p.get(CHECKOUT_STAGE_QUERY_KEY) === stepStage) return prev
          p.set(CHECKOUT_STAGE_QUERY_KEY, stepStage)
          return p
        },
        { replace: true }
      )
    }
  }, [searchParams, step, cartCount, setSearchParams, setStep])

  // ✅ عرض السلة الفارغة بعد جميع الـ hooks لتفادي خطأ "Rendered fewer hooks than expected"
  if (cartCount === 0) {
    return (
      <S.CheckoutPageContainer>
        <S.CheckoutWrapper>
          <S.Header>
            <S.BackButton onClick={backToStore}>
              <ArrowRight size={18} />
              <span>رجوع للمتجر</span>
            </S.BackButton>
            <S.HeaderTitleWrapper>
              <ShoppingBag size={24} />
              <S.Title>سلة المشتريات</S.Title>
            </S.HeaderTitleWrapper>
          </S.Header>
          
          <S.EmptyCartContainer>
            <S.EmptyCartIcon>
              <ShoppingBag size={64} strokeWidth={1} />
            </S.EmptyCartIcon>
            <S.EmptyCartText>سلة المشتريات فارغة</S.EmptyCartText>
            <S.PayButton onClick={backToStore} style={{ maxWidth: '200px', marginTop: 0 }}>
              تصفح المنتجات
            </S.PayButton>
          </S.EmptyCartContainer>
        </S.CheckoutWrapper>
      </S.CheckoutPageContainer>
    )
  }

  // دوال التحقق الفوري لكل حقل
  const validateName = (name: string): string | null => {
    if (!name?.trim()) return 'يرجى إدخال الأسم'
    if (name.trim().length < 2) return 'الاسم يجب أن يكون على الأقل حرفين'
    return null
  }

  const validateEmail = (email: string): string | null => {
    if (!email?.trim()) return 'يرجى إدخال البريد الإلكتروني'
    if (!/\S+@\S+\.\S+/.test(email)) return 'البريد الإلكتروني غير صالح'
    return null
  }

  const validatePhone = (phone: string): string | null => {
    if (!phone?.trim()) return 'يرجى إدخال رقم الهاتف'
    if (phone.length < 9) return 'رقم الهاتف يجب أن يكون صحيحاً'
    if (!/^05\d{8}$/.test(phone)) return 'رقم الهاتف يجب أن يبدأ بـ 05 ويحتوي على 10 أرقام'
    return null
  }

  const validateCity = (city: string): string | null => {
    if (!city?.trim()) return 'يرجى اختيار المدينة'
    return null
  }

  const validateAddress = (address: string): string | null => {
    if (!address?.trim()) return 'يرجى إدخال العنوان'
    if (address.trim().length < 5) return 'العنوان يجب أن يكون على الأقل 5 أحرف'
    return null
  }

  // التحقق من البيانات الأساسية فقط (الاسم، البريد، الهاتف)
  const validateBasicCustomerDetails = () => {
    const newErrors: Record<string, string> = {}
    const nameError = validateName(customerDetails.name)
    const emailError = validateEmail(customerDetails.email)
    const phoneError = validatePhone(customerDetails.phone)
    
    if (nameError) newErrors.name = nameError
    if (emailError) newErrors.email = emailError
    if (phoneError) newErrors.phone = phoneError
    
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  // التحقق من العنوان
  const validateAddressDetails = () => {
    const newErrors: Record<string, string> = {}
    const cityError = validateCity(customerDetails.address?.city)
    const addressError = validateAddress(customerDetails.address?.line1)
    
    if (cityError) newErrors.city = cityError
    if (addressError) newErrors.line1 = addressError
    
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  // التحقق الكامل من جميع البيانات
  const validateCustomerDetails = () => {
    const basicValid = validateBasicCustomerDetails()
    const addressValid = validateAddressDetails()
    return basicValid && addressValid
  }

  const handleSelectMethod = (method: 'mada' | 'credit' | 'applepay') => {
    // التحقق من العنوان قبل السماح باختيار طريقة الدفع
    if (!validateAddressDetails()) {
      return
    }
    
    // ✅ منع إرسال الحدث إذا كانت نفس طريقة الدفع
    if (lastTrackedPaymentMethod.current === method) {
      setPaymentMethod(method)
      setStep(PAYMENT_STEPS.CARD_FORM)
      return
    }
    
    setPaymentMethod(method)
    setStep(PAYMENT_STEPS.CARD_FORM)
    
    // ✅ تحديث آخر طريقة دفع تم تتبعها
    lastTrackedPaymentMethod.current = method
    
    // Track Add Payment Info - مرة واحدة فقط لكل طريقة دفع
    const shippingCost = storeData?.shipping
    eventHub.track('ADD_BILLING', {
      value: cartTotal + (shippingCost ?? 0),
      currency: 'SAR',
      form_type: 'payment_method',
      form_id: method,
      // ✅ إضافة items لـ Meta CAPI (Item Category & Item IDs)
      items: cartItems.map(item => ({
        item_id: item.id.toString(),
        item_name: item.title,
        price: item.price,
        quantity: item.quantity,
        item_category: item.category || 'Sweets'
      }))
    })
  }

  const handleTokenize = async () => {
    // ✅ المتجر مشغول: إيقاف الدفع وإظهار التنبيه
    if (storeBusy) {
      setBusyModalDismissed(false)
      return
    }
    // ✅ إعادة تعيين hasNavigatedRef عند بدء عملية دفع جديدة
    hasNavigatedRef.current = false
    // التحقق من صحة الكوبون قبل إتمام الدفع
    if (appliedCoupon) {
      const result = await validateCoupon(appliedCoupon, cartTotal)
      if (!result.valid) {
        // الكوبون لم يعد صالحاً - إزالته وإيقاف الدفع
        setDiscount(0)
        setFreeShipping(false)
        setAppliedCoupon(null)
        setCouponCode('')
        toast.error(result.message || 'عذراً، هذا الكوبون لم يعد صالحاً للاستخدام. يرجى إزالة الكوبون والمحاولة مرة أخرى', {
          id: 'coupon-expired',
          duration: 7000,
          style: TOAST_STYLE,
          iconTheme: TOAST_ERROR_ICON,
        })
        return
      } else if (result.discount !== discount) {
        // تحديث قيمة الخصم والشحن المجاني إذا تغيرت
        if (result.discount !== discount) {
          setDiscount(result.discount)
        }
        if (result.freeShipping !== freeShipping) {
          setFreeShipping(result.freeShipping)
        }
        if (result.discount !== discount || result.freeShipping !== freeShipping) {
          toast.success('تم تحديث قيمة الخصم', {
            id: 'coupon-updated',
            style: TOAST_STYLE,
            iconTheme: TOAST_SUCCESS_ICON,
          })
        }
      }
    }
    
    if (window.CardSDK) window.CardSDK.tokenize()
  }
  
  // Wrapper function للتحقق من الكوبون قبل استدعاء processTapPayment
  const handlePaymentWithCouponValidation = async (tokenId: string) => {
    // ✅ إعادة تعيين hasNavigatedRef عند بدء عملية دفع جديدة
    hasNavigatedRef.current = false

    // ✅ فحص نهائي قبل الدفع: إزالة أي منتج صار نفذ وإيقاف الدفع إذا وُجد
    const removed = await syncPrices()
    if (removed.length > 0) {
      const names = removed.map(r => r.title).join('، ')
      toast.error(`تمت إزالة من السلة (نفذت): ${names}. يرجى مراجعة السلة ثم إعادة المحاولة.`, {
        id: 'sync-removed',
        duration: 7000,
        style: TOAST_STYLE,
        iconTheme: TOAST_ERROR_ICON,
      })
      return
    }

    // التحقق النهائي من الكوبون قبل إتمام الدفع
    if (appliedCoupon) {
      const result = await validateCoupon(appliedCoupon, cartTotal)
      if (!result.valid) {
        // الكوبون لم يعد صالحاً - إزالته وإيقاف الدفع
        setDiscount(0)
        setFreeShipping(false)
        setAppliedCoupon(null)
        setCouponCode('')
        toast.error(result.message || 'عذراً، هذا الكوبون لم يعد صالحاً للاستخدام. يرجى المحاولة مرة أخرى', {
          id: 'coupon-expired',
          duration: 7000,
          style: TOAST_STYLE,
          iconTheme: TOAST_ERROR_ICON,
        })
        return
      }
      // تحديث الشحن المجاني
      setFreeShipping(result.freeShipping)
      // استخدام قيمة الخصم المحدثة والشحن المجاني
      // ✅ الشحن المجاني بالحد الأدنى يبقى سارياً مع الكوبون — وإلا احتُسب شحن لم يظهر في الإجمالي
      const finalShipping = result.freeShipping || freeShippingInfo.reachedThreshold ? 0 : (shipping ?? 0)
      // حساب المبلغ الإجمالي (✅ المبالغ الصغيرة مسموح بها)
      const calculatedFinalTotal = cartTotal + finalShipping - result.discount
      const finalTotal = calculatedFinalTotal <= 0 ? 0.00 : calculatedFinalTotal
      await processTapPayment(tokenId, finalTotal, appliedCoupon, cartTotal, finalShipping)
    } else {
      // لا يوجد كوبون - المتابعة بالطريقة العادية
      await processTapPayment(tokenId, total, undefined, cartTotal, shippingCost)
    }
  }

  const handleCustomerSubmit = async (e?: React.FormEvent) => {
    // ✅ المتجر مشغول: إيقاف المتابعة للدفع وإظهار التنبيه
    if (storeBusy) {
      setBusyModalDismissed(false)
      return
    }
    // التحقق من البيانات الأساسية فقط (الاسم، البريد، الهاتف)
    if (!validateBasicCustomerDetails()) {
      // إظهار رسالة خطأ للمستخدم
      toast.error('يرجى إدخال جميع البيانات المطلوبة بشكل صحيح', {
        id: 'validation-error',
        style: TOAST_STYLE,
        iconTheme: TOAST_ERROR_ICON,
      })
      return
    }

    setIsSubmittingCustomer(true)

    // ✅ قبل المتابعة للدفع: مزامنة السلة وإزالة المنتجات النفذة
    const removed = await syncPrices()
    if (removed.length > 0) {
      const names = removed.map(r => r.title).join('، ')
      toast.error(`تمت إزالة من السلة (نفذت): ${names}`, {
        id: 'sync-removed',
        duration: 6000,
        style: TOAST_STYLE,
        iconTheme: TOAST_ERROR_ICON,
      })
    }

    // Identify user
    const nameParts = customerDetails.name.trim().split(/\s+/);
    const firstName = nameParts[0];
    const lastName = nameParts.slice(1).join(' ') || '';

    // ✅ تسجيل العميل في قاعدة البيانات عند ملء بياناته الأساسية
    // ملاحظة: العنوان سيتم إرساله لاحقاً عند الدفع
    const BACKEND_URL = import.meta.env.VITE_BACKEND_URL || 'https://app.teen-sa.com'
    const customerData = {
      email: customerDetails.email,
      phone: customerDetails.phone,
      first_name: firstName,
      last_name: lastName,
      full_name: customerDetails.name.trim(),
      address: {
        line1: '', // العنوان سيتم إرساله لاحقاً عند الدفع
        city: '',
        state: customerDetails.address.state || '',
        country: customerDetails.address.country || 'SA',
        zip: ''
      }
    }

    // ✅ CRITICAL: يجب إرسال SIGN_UP فقط بعد نجاح حفظ البيانات في قاعدة البيانات
    // هذا يضمن أن عدد أحداث CompleteRegistration يطابق عدد العملاء الفعليين في قاعدة البيانات
    let isNewCustomer = false
    let customerSaved = false
    let registeredCustomerUserId: string | undefined

    try {
      const response = await fetch(`${BACKEND_URL}/api/customers/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(customerData),
      })

      if (response.ok) {
        const data = await response.json()
        if (data.success) {
          customerSaved = true // ✅ تم حفظ البيانات بنجاح في قاعدة البيانات

          const cid = data.data?.customer_id
          if (cid != null && cid !== '') {
            registeredCustomerUserId = String(cid)
          }

          // ✅ التحقق من أن العميل جديد باستخدام flag is_new من Backend
          // هذا يضمن أن SIGN_UP يُرسل فقط عند إنشاء عميل جديد
          // وليس عند تحديث عميل موجود (يقلل التكرار في أحداث CompleteRegistration)
          if (data.data && data.data.is_new === true) {
            isNewCustomer = true
          }
        } else {
          customerSaved = false
        }
      } else {
        customerSaved = false
      }
    } catch (error) {
      customerSaved = false
      // لا نوقف العملية، نكمل حتى لو فشل التسجيل
    }

    if (!customerSaved || !registeredCustomerUserId) {
      setIsSubmittingCustomer(false)
      toast.error('تعذّر ربط بياناتك بحساب عميل. تحقق من الاتصال أو البيانات ثم أعد المحاولة.', {
        id: 'customer-register-required',
        style: TOAST_STYLE,
        iconTheme: TOAST_ERROR_ICON,
      })
      return
    }

    // ✅ الانتقال لخطوة الدفع فوراً - لا ننتظر تتبع الأحداث
    submitCustomerDetails()
    setIsSubmittingCustomer(false)

    // ✅ تتبع الأحداث بشكل غير متزامن (لا يمنع الانتقال للخطوة التالية)
    // ملاحظة: العنوان (city) سيتم إرساله لاحقاً عند الدفع
    // ✅ Best Practice: Call identify() before tracking events (EMQ improvement)
    // ✅ حماية ضد التكرار: لا نرسل IDENTIFY و START_CHECKOUT إذا تم إرسالهما بالفعل
    if (hasTrackedCheckout.current) {
      return
    }
    hasTrackedCheckout.current = true
    
    ;(async () => {
      try {
        await eventHub.identify({
          ...(registeredCustomerUserId ? { user_id: registeredCustomerUserId } : {}),
          first_name: firstName,
          last_name: lastName,
          email: customerDetails.email,
          phone: customerDetails.phone,
          country: 'SA'
          // city و zip: سيتم إرسالهما لاحقاً عند الدفع
        });

        // ✅ CRITICAL: Track Lead/Signup - فقط للعملاء الجدد وبعد نجاح حفظ البيانات
        // هذا يضمن أن عدد أحداث CompleteRegistration يطابق عدد العملاء الفعليين في قاعدة البيانات
        // لا يُرسل SIGN_UP إذا:
        // 1. فشل حفظ البيانات في قاعدة البيانات (customerSaved = false)
        // 2. العميل موجود بالفعل (isNewCustomer = false)
        if (customerSaved && isNewCustomer) {
          eventHub.signUp({
            method: 'checkout_form'
          });
        }

        persistPurchaseDeviceContext()

        // Track Initiate Checkout
        const shippingCost = storeData?.shipping
        eventHub.startCheckout({
          value: cartTotal + (shippingCost ?? 0),
          currency: 'SAR',
          items: cartItems.map(item => ({
            item_id: item.id.toString(), // ✅ تحويل إلى string
            item_name: item.title,
            price: item.price,
            quantity: item.quantity,
            item_category: item.category || 'Sweets' // ✅ إضافة item_category لـ Meta CAPI
          }))
        });
      } catch (trackingError) {
        // لا نوقف العملية، نكمل حتى لو فشل التتبع
      }
    })()
  }

  return (
    <S.CheckoutPageContainer>
      <S.CheckoutWrapper>
        {/* Header */}
        <S.Header>
          <S.BackButton onClick={backToStore}>
            <ArrowRight size={18} />
            <span>رجوع للمتجر</span>
          </S.BackButton>
          <S.HeaderTitleWrapper>
            <ShoppingBag size={24} />
            <S.Title>إتمام الطلب</S.Title>
          </S.HeaderTitleWrapper>
        </S.Header>

        <S.Content>
          {/* Left Column - Order Summary */}
          <S.LeftColumn>
            <S.SummaryCard>
              <S.SummaryHeader>
                <S.SummaryTitle>ملخص الطلب ({cartCount} منتجات)</S.SummaryTitle>
              </S.SummaryHeader>

              {cartItems.map((item) => (
                <S.ProductSection key={item.id}>
                  <S.ProductImageWrapper>
                    <S.ProductImage
                      src={
                        normalizeMediaUrl(item.image) ||
                        'https://via.placeholder.com/200x200?text=%D9%85%D9%86%D8%AA%D8%AA%D8%AC'
                      }
                      alt={item.title}
                      loading="lazy"
                    />
                  </S.ProductImageWrapper>
                  <S.ProductInfo>
                    <S.ProductTitle>{item.title}</S.ProductTitle>
                    <S.QuantityWrapper>
                      <S.ProductQuantity>الكمية: {item.quantity}</S.ProductQuantity>
                      {editingItemId !== item.id && (
                        <S.EditButton onClick={() => handleEditQuantity(item.id)} aria-label="تعديل الكمية">
                          تعديل
                        </S.EditButton>
                      )}
                    </S.QuantityWrapper>
                    <div style={{ fontSize: '14px', color: 'oklch(55.44% 0.041 257.42)', marginTop: '4px' }}>
                       {item.price.toFixed(2)} ريال
                    </div>
                  </S.ProductInfo>
                  {editingItemId === item.id ? (
                    <S.QuantityControls>
                      <S.QuantityButton onClick={() => handleUpdateQuantity(item.id, item.quantity - 1)}>
                        <Minus size={16} />
                      </S.QuantityButton>
                      <S.QuantityValue>{item.quantity}</S.QuantityValue>
                      <S.QuantityButton onClick={() => handleUpdateQuantity(item.id, item.quantity + 1)}>
                        <Plus size={16} />
                      </S.QuantityButton>
                      <S.CancelEditButton onClick={handleCancelEdit} aria-label="إلغاء التعديل">
                        <X size={16} />
                      </S.CancelEditButton>
                    </S.QuantityControls>
                  ) : (
                    <S.RemoveButton onClick={() => handleRemoveItem(item.id)} aria-label="حذف المنتج">
                      <Trash2 size={18} />
                    </S.RemoveButton>
                  )}
                </S.ProductSection>
              ))}

              <S.Divider style={{ display: 'block', margin: '16px 0', borderTop: '1px solid oklch(0.9683 0.0069 247.9)' }} />

              <S.SummaryDetails>
                <S.SummaryItem>
                  <S.SummaryLabel>قيمة الطلب</S.SummaryLabel>
                  <S.SummaryValue>
                    {cartTotal.toFixed(2)}
                    <S.RiyalIcon><SaudiRiyal size={16} /></S.RiyalIcon>
                  </S.SummaryValue>
                </S.SummaryItem>
                <S.SummaryItem>
                  <S.SummaryLabel>رسوم التوصيل</S.SummaryLabel>
                  <S.SummaryValue>
                    {!hasStoreData ? (
                      <span style={{ color: 'oklch(0.7107 0.0351 256.79)' }}>—</span>
                    ) : effectiveFreeShipping || (shipping !== undefined && shipping === 0) ? (
                      <S.FreeShippingText>مجاني</S.FreeShippingText>
                    ) : shipping != null && shipping > 0 ? (
                      <>
                        <span style={{ color: 'oklch(0.2077 0.0398 265.75)' }}>{shippingCost.toFixed(2)}</span>
                        <S.RiyalIcon><SaudiRiyal size={16} /></S.RiyalIcon>
                      </>
                    ) : (
                      <S.FreeShippingText>مجاني</S.FreeShippingText>
                    )}
                  </S.SummaryValue>
                </S.SummaryItem>
                {amountLeftForFreeShipping > 0 && (
                  <S.FreeShippingHint>
                    تبقى لك {amountLeftForFreeShipping} ريال ويصير الشحن مجاني
                  </S.FreeShippingHint>
                )}
                {discount > 0 && (
                  <S.DiscountRow>
                    <span>الخصم</span>
                    <span>
                      -{discount.toFixed(2)}
                      <S.RiyalIcon><SaudiRiyal size={14} /></S.RiyalIcon>
                    </span>
                  </S.DiscountRow>
                )}
                <S.TotalRow>
                  <S.TotalLabel>الإجمالي</S.TotalLabel>
                  <S.TotalValue>
                    {total.toFixed(2)}
                    <S.RiyalIcon><SaudiRiyal size={18} /></S.RiyalIcon>
                  </S.TotalValue>
                </S.TotalRow>
              </S.SummaryDetails>

              <S.CouponSection>
                {appliedCoupon ? (
                  <S.AppliedCouponInfo>
                    <div>
                      <span style={{ color: 'oklch(55.44% 0.041 257.42)', fontSize: '12px', marginLeft: '8px' }}>كوبون مطبق:</span>
                      <S.CouponCodeText>
                        <S.CheckIconWrapper>
                          <Check size={12} color="oklch(1 0 0)" />
                        </S.CheckIconWrapper>
                        {appliedCoupon}
                      </S.CouponCodeText>
                    </div>
                    <S.RemoveCouponButton onClick={handleRemoveCoupon}>
                      إلغاء
                    </S.RemoveCouponButton>
                  </S.AppliedCouponInfo>
                ) : (
                  <>
                    <S.CouponWrapper>
                      <S.CouponInput
                        type="text"
                        placeholder="أدخل كود الخصم"
                        value={couponCode}
                        onChange={(e) => setCouponCode(e.target.value)}
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') {
                            handleApplyCoupon()
                          }
                        }}
                      />
                      <S.ApplyCouponButton onClick={handleApplyCoupon}>
                        تطبيق
                      </S.ApplyCouponButton>
                    </S.CouponWrapper>
                  </>
                )}
              </S.CouponSection>
            </S.SummaryCard>
          </S.LeftColumn>

          {/* Right Column - Payment */}
          <S.RightColumn>
            {step === PAYMENT_STEPS.CUSTOMER_DETAILS || step === PAYMENT_STEPS.INITIAL ? (
              <S.PaymentCard>
                <S.PaymentHeader>
                  <S.Steps>
                    <S.Step $active={true}>
                      <S.StepNumber>1</S.StepNumber>
                      <S.StepTitle>بيانات العميل</S.StepTitle>
                    </S.Step>
                    
                    <S.StepDivider />
                    
                    <S.Step>
                      <S.StepNumber>2</S.StepNumber>
                      <S.StepTitle>الدفع</S.StepTitle>
                    </S.Step>
                  </S.Steps>
                  <S.PaymentSubtitle>يرجى إدخال بياناتك لإتمام الطلب</S.PaymentSubtitle>
                </S.PaymentHeader>

                <S.CustomerForm onSubmit={async (e) => {
                  e.preventDefault();
                  try {
                    await handleCustomerSubmit(e);
                  } catch (error) {
                    toast.error('حدث خطأ أثناء معالجة الطلب. يرجى المحاولة مرة أخرى.', {
                      id: 'submit-error',
                      style: TOAST_STYLE,
                      iconTheme: TOAST_ERROR_ICON,
                    });
                  }
                }}>
                  <S.FormGroup>
                    <S.FormLabel>الأسم</S.FormLabel>
                    <S.Input
                      type="text"
                      autoComplete="name"
                      value={customerDetails.name}
                      onChange={(e) => {
                        const value = e.target.value
                        setCustomerDetails({ name: value })
                        // التحقق الفوري أثناء الكتابة
                        const error = validateName(value)
                        setErrors({ ...errors, name: error || null })
                      }}
                      onBlur={(e) => {
                        // التحقق مرة أخرى عند الخروج من الحقل
                        const error = validateName(e.target.value)
                        setErrors({ ...errors, name: error || null })
                      }}
                      placeholder="أدخل اسمك"
                      $hasError={!!errors.name}
                    />
                    {errors.name && <S.ErrorMessage>{errors.name}</S.ErrorMessage>}
                  </S.FormGroup>
                  <S.FormGroup>
                    <S.FormLabel>البريد الإلكتروني</S.FormLabel>
                    <S.Input
                      type="email"
                      autoComplete="email"
                      style={{ direction: 'ltr' }}
                      value={customerDetails.email}
                      onChange={(e) => {
                        const value = e.target.value
                        setCustomerDetails({ email: value })
                        // التحقق الفوري أثناء الكتابة (بعد إدخال @)
                        if (value.includes('@')) {
                          const error = validateEmail(value)
                          setErrors({ ...errors, email: error || null })
                        } else if (value.trim() === '') {
                          // إذا كان الحقل فارغاً، أزل رسالة الخطأ
                          setErrors({ ...errors, email: null })
                        }
                      }}
                      onBlur={(e) => {
                        // التحقق مرة أخرى عند الخروج من الحقل
                        const error = validateEmail(e.target.value)
                        setErrors({ ...errors, email: error || null })
                      }}
                      placeholder="name@example.com"
                      $hasError={!!errors.email}
                    />
                    {errors.email && <S.ErrorMessage>{errors.email}</S.ErrorMessage>}
                    <S.HelperText>
                      <Info size={14} />
                      لاستلام تفاصيل الطلب
                    </S.HelperText>
                  </S.FormGroup>
                  <S.FormGroup>
                    <S.FormLabel>رقم الهاتف</S.FormLabel>
                    <S.PhoneInput
                      type="tel"
                      autoComplete="tel"
                      value={customerDetails.phone}
                      onChange={(e) => {
                        const val = e.target.value.replace(/[٠-٩]/g, d => String('٠١٢٣٤٥٦٧٨٩'.indexOf(d))).replace(/\D/g, '')
                        if (val.length <= 10) {
                          setCustomerDetails({ phone: val })
                          // التحقق الفوري أثناء الكتابة (بعد إدخال 9 أرقام على الأقل)
                          if (val.length >= 9) {
                            const error = validatePhone(val)
                            setErrors({ ...errors, phone: error || null })
                          } else if (val.length === 0) {
                            // إذا كان الحقل فارغاً، أزل رسالة الخطأ
                            setErrors({ ...errors, phone: null })
                          }
                        }
                      }}
                      onBlur={(e) => {
                        // التحقق مرة أخرى عند الخروج من الحقل
                        const error = validatePhone(e.target.value)
                        setErrors({ ...errors, phone: error || null })
                      }}
                      placeholder="05xxxxxxxx"
                      $hasError={!!errors.phone}
                    />
                    {errors.phone && <S.ErrorMessage>{errors.phone}</S.ErrorMessage>}
                    <S.HelperText>
                      <Info size={14} />
                      يرجى التأكد من صحة الرقم لضمان تواصل المندوب معك بنجاح
                    </S.HelperText>
                  </S.FormGroup>

                  <S.PayButton
                    type="submit"
                    disabled={isSubmittingCustomer || storeBusy}
                  >
                    {storeBusy
                      ? 'المتجر مشغول حالياً'
                      : isSubmittingCustomer
                        ? 'جاري المعالجة...'
                        : 'المتابعة للدفع'}
                  </S.PayButton>
                </S.CustomerForm>
              </S.PaymentCard>
            ) : (
              <S.PaymentCard>
                <S.PaymentHeader>
                  <S.Steps>
                    <S.Step $completed={true} onClick={goBack}>
                      <S.StepNumber><Check size={20} /></S.StepNumber>
                      <S.StepTitle>بيانات العميل</S.StepTitle>
                    </S.Step>
                    
                    <S.StepDivider $active={true} />
                    
                    <S.Step $active={true}>
                      <S.StepNumber>2</S.StepNumber>
                      <S.StepTitle>الدفع</S.StepTitle>
                    </S.Step>
                  </S.Steps>
                  <S.PaymentSubtitle>يرجى إدخال العنوان ثم اختر طريقة الدفع</S.PaymentSubtitle>
                </S.PaymentHeader>

                {/* العنوان - منفصل بصرياً */}
                <S.CustomerForm onSubmit={(e) => {
                  e.preventDefault();
                }}>
                  <S.SectionSubtitle>العنوان</S.SectionSubtitle>
                  <S.FormGroup>
                    <S.FormLabel>المدينة</S.FormLabel>
                    <S.Select
                      value={customerDetails.address.city}
                      onChange={(e) => {
                        const value = e.target.value
                        setCustomerDetails({ address: { ...customerDetails.address, city: value } })
                        // التحقق الفوري عند اختيار المدينة
                        const error = validateCity(value)
                        setErrors({ ...errors, city: error || null })
                      }}
                      onBlur={(e) => {
                        // التحقق مرة أخرى عند الخروج من الحقل
                        const error = validateCity(e.target.value)
                        setErrors({ ...errors, city: error || null })
                      }}
                      $hasError={!!errors.city}
                    >
                      <option value="">اختر المدينة</option>
                      {storeData?.branches
                        ?.filter(b => !b.comingSoon)
                        .map(branch => (
                          <option key={branch.id} value={branch.name}>{branch.name}</option>
                        ))
                      }
                    </S.Select>
                    {errors.city && <S.ErrorMessage>{errors.city}</S.ErrorMessage>}
                  </S.FormGroup>
                  <S.FormGroup>
                    <S.FormLabel style={{ display: 'flex', alignItems: 'center' }}>
                      العنوان المختصر / الشارع / الحي
                      <span style={{ color: 'oklch(0.61 0.25 28.5)', marginRight: '4px', marginLeft: '4px' }}>*</span>
                      <S.TooltipWrapper>
                        <S.TooltipIconWrapper>
                          <Info size={14} />
                        </S.TooltipIconWrapper>
                        <S.TooltipContent>
                          يمكنك الحصول على رمز العنوان الوطني المختصر من حسابك على موقع سبل
                        </S.TooltipContent>
                      </S.TooltipWrapper>
                    </S.FormLabel>
                    <S.Input
                      type="text"
                      autoComplete="street-address"
                      value={customerDetails.address.line1}
                      onChange={(e) => {
                        const value = e.target.value
                        setCustomerDetails({ address: { ...customerDetails.address, line1: value } })
                        // التحقق الفوري أثناء الكتابة (بعد إدخال 5 أحرف على الأقل)
                        if (value.trim().length >= 5) {
                          const error = validateAddress(value)
                          setErrors({ ...errors, line1: error || null })
                        } else if (value.trim().length === 0) {
                          // إذا كان الحقل فارغاً، أزل رسالة الخطأ
                          setErrors({ ...errors, line1: null })
                        }
                      }}
                      onBlur={(e) => {
                        // التحقق مرة أخرى عند الخروج من الحقل
                        const error = validateAddress(e.target.value)
                        setErrors({ ...errors, line1: error || null })
                      }}
                      placeholder="العنوان المختصر / اسم الشارع، رقم المبنى"
                      $hasError={!!errors.line1}
                    />
                    {errors.line1 && <S.ErrorMessage>{errors.line1}</S.ErrorMessage>}
                  </S.FormGroup>
                </S.CustomerForm>

                {/* طرق الدفع - منفصلة بصرياً */}
                <S.SectionSubtitle style={{ marginTop: '0' }}>طريقة الدفع</S.SectionSubtitle>
                <S.PaymentOptions>
                {/* Apple Pay - الخيار الأول إذا كان متاحاً */}
                {isSafari && (
                <S.PaymentOption $isSelected={paymentMethod === 'applepay'}>
                  <S.OptionLabel>
                    <input
                      type="radio"
                      name="paymentMethod"
                      value="applepay"
                      checked={paymentMethod === 'applepay'}
                      onChange={() => handleSelectMethod('applepay')}
                    />
                    <S.RadioCircle />
                    <S.OptionContent>
                      <S.OptionText>Apple Pay</S.OptionText>
                      <S.OptionLogo
                        src={applePayLogo}
                        alt="Apple Pay"
                        style={{ height: '24px', objectFit: 'contain' }}
                      />
                    </S.OptionContent>
                  </S.OptionLabel>

                  {paymentMethod === 'applepay' && (
                    <S.OptionForm>
                      <S.FormWrapper>
                        <TapApplePay
                          key="applepay"
                          onTokenSuccess={(tokenId) => handlePaymentWithCouponValidation(tokenId)}
                          amount={total}
                          currency="SAR"
                          customerDetails={customerDetails}
                        />
                      </S.FormWrapper>
                    </S.OptionForm>
                  )}
                </S.PaymentOption>
                )}

                <S.PaymentOption $isSelected={paymentMethod === 'mada'}>
                  <S.OptionLabel>
                    <input
                      type="radio"
                      name="paymentMethod"
                      value="mada"
                      checked={paymentMethod === 'mada'}
                      onChange={() => handleSelectMethod('mada')}
                    />
                    <S.RadioCircle />
                    <S.OptionContent>
                      <S.OptionText>بطاقة مدى البنكية</S.OptionText>
                      <S.OptionLogo
                        src="https://media.zid.store/static/mada-circle.png"
                        alt="مدى"
                      />
                    </S.OptionContent>
                  </S.OptionLabel>

                  {paymentMethod === 'mada' && (
                    <S.OptionForm>
                      <S.FormWrapper>
                        <TapCardV2
                          mountId="card-sdk-mada"
                          hideSubmit
                          onTokenSuccess={(tokenId) => handlePaymentWithCouponValidation(tokenId)}
                          amount={total}
                          currency="SAR"
                          customerDetails={customerDetails}
                        />
                        <S.PayButton
                          type="button"
                          onClick={handleTokenize}
                          disabled={isProcessing || storeBusy}
                        >
                          {storeBusy
                            ? 'المتجر مشغول حالياً'
                            : isProcessing
                              ? 'جاري المعالجة...'
                              : `إتمام الدفع ${total.toFixed(2)} ريال`}
                        </S.PayButton>
                      </S.FormWrapper>
                    </S.OptionForm>
                  )}
                </S.PaymentOption>

                <S.PaymentOption $isSelected={paymentMethod === 'credit'}>
                  <S.OptionLabel>
                    <input
                      type="radio"
                      name="paymentMethod"
                      value="credit"
                      checked={paymentMethod === 'credit'}
                      onChange={() => handleSelectMethod('credit')}
                    />
                    <S.RadioCircle />
                    <S.OptionContent>
                      <S.OptionText>بطاقة إئتمانية</S.OptionText>
                      <S.OptionLogo
                        src={visaMasterImg}
                        alt="VISA MasterCard"
                      />
                    </S.OptionContent>
                  </S.OptionLabel>

                  {paymentMethod === 'credit' && (
                    <S.OptionForm>
                      <S.FormWrapper>
                        <TapCardV2
                          mountId="card-sdk-credit"
                          hideSubmit
                          onTokenSuccess={(tokenId) => handlePaymentWithCouponValidation(tokenId)}
                          amount={total}
                          currency="SAR"
                          customerDetails={customerDetails}
                        />
                        <S.PayButton
                          type="button"
                          onClick={handleTokenize}
                          disabled={isProcessing || storeBusy}
                        >
                          {storeBusy
                            ? 'المتجر مشغول حالياً'
                            : isProcessing
                              ? 'جاري المعالجة...'
                              : `إتمام الدفع ${total.toFixed(2)} ريال`}
                        </S.PayButton>
                      </S.FormWrapper>
                    </S.OptionForm>
                  )}
                </S.PaymentOption>
                </S.PaymentOptions>

                <S.FormBackButton onClick={goBack}>
                  رجوع لتعديل البيانات
                </S.FormBackButton>
              </S.PaymentCard>
            )}
          </S.RightColumn>
        </S.Content>

        {paymentResult && !paymentResult.success && (
          <S.ResultOverlay>
            <S.ResultModal>
              <h3>{paymentResult.message}</h3>
              <S.CloseButton onClick={() => setPaymentResult(null)}>إغلاق</S.CloseButton>
            </S.ResultModal>
          </S.ResultOverlay>
        )}

        {storeBusy && !busyModalDismissed && (
          <S.ResultOverlay>
            <S.BusyModal>
              <S.BusyIconBadge>
                <Clock size={34} strokeWidth={1.75} />
              </S.BusyIconBadge>
              <S.BusyTitle>المتجر مشغول حالياً</S.BusyTitle>
              <S.BusyText>
                نعتذر، يستقبل المتجر ضغطاً كبيراً على الطلبات في الوقت الحالي.
                يرجى المحاولة مرة أخرى بعد قليل.
              </S.BusyText>
              <S.BusyButton onClick={() => setBusyModalDismissed(true)}>
                حسناً
              </S.BusyButton>
            </S.BusyModal>
          </S.ResultOverlay>
        )}

        {showDeleteConfirm && (
          <S.ResultOverlay>
            <S.ResultModal>
              <div style={{ marginBottom: '20px', color: 'oklch(0.61 0.25 28.5)' }}>
                <AlertTriangle size={48} strokeWidth={1.5} />
              </div>
              <h3>هل أنت متأكد من حذف المنتج من السلة؟</h3>
              <S.ModalActions>
                <S.CloseButton onClick={() => setShowDeleteConfirm(false)}>
                  تراجع
                </S.CloseButton>
                <S.ConfirmDeleteButton onClick={confirmDelete}>
                  حذف المنتج
                </S.ConfirmDeleteButton>
              </S.ModalActions>
            </S.ResultModal>
          </S.ResultOverlay>
        )}
      </S.CheckoutWrapper>
    </S.CheckoutPageContainer>
  )
}
